import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Trash2, FileText } from 'lucide-react';
import { useReport, useDeleteReport, useExportReport } from '@/hooks/reporting/useReports';
import ExportButtons from '@/components/reports/common/ExportButtons';
import EmptyState from '@/components/reports/common/EmptyState';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';

export default function ReportDetail() {
  const { snapshotId } = useParams<{ snapshotId: string }>();
  const navigate = useNavigate();
  const deleteReport = useDeleteReport();
  const exportReport = useExportReport();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: report, isLoading, error } = useReport(snapshotId);

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    await deleteReport.mutateAsync(snapshotId!);
    setIsDeleteModalOpen(false);
    navigate('/admin/reporting/reports/saved');
  };

  const handleExportPDF = async () => {
    await exportReport.mutateAsync({ snapshotId: snapshotId!, format: 'pdf' });
  };

  const handleExportExcel = async () => {
    await exportReport.mutateAsync({ snapshotId: snapshotId!, format: 'excel' });
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error loading report</h3>
          <p className="text-red-600 text-sm mt-1">{(error as Error).message}</p>
          <button
            onClick={() => navigate('/admin/reporting/reports/saved')}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
          >
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-4 bg-gray-200 rounded w-2/3" />
            <div className="h-4 bg-gray-200 rounded w-1/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-6">
        <EmptyState
          icon={FileText}
          title="Report not found"
          description="The report you're looking for doesn't exist or has been deleted."
          action={{
            label: 'Back to Reports',
            onClick: () => navigate('/admin/reporting/reports/saved'),
          }}
        />
      </div>
    );
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatReportType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderReportContent = () => {
    const content: React.ReactNode[] = [];
    const reportData = report as any;

    // Render Aggregate Metrics
    if (reportData.aggregate_metrics) {
      content.push(
        <div key="aggregate" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Period Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(reportData.aggregate_metrics).map(([key, value]: [string, any]) => (
              <div key={key} className="p-4 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-600 capitalize">
                  {key.replace(/_/g, ' ')}
                </div>
                <div className="text-xl font-bold text-gray-900 mt-1">
                  {typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Render Comparison Period
    if (reportData.comparison_period?.metrics) {
      content.push(
        <div key="comparison" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Previous Period Metrics ({reportData.comparison_period.period?.label || 'Previous Period'})
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(reportData.comparison_period.metrics).map(([key, value]: [string, any]) => (
              <div key={key} className="p-4 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-600 capitalize">
                  {key.replace(/_/g, ' ')}
                </div>
                <div className="text-xl font-bold text-gray-900 mt-1">
                  {typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Render Department Breakdowns
    if (reportData.dimensional_breakdowns?.by_department && reportData.dimensional_breakdowns.by_department.length > 0) {
      content.push(
        <div key="departments" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Department Performance</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Users</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active Users</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completion Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engagement Score</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.dimensional_breakdowns.by_department.map((dept: any, index: number) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{dept.department_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{dept.total_users}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{dept.active_users}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{dept.metrics?.avg_completion_rate?.toFixed(2)}%</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{dept.metrics?.engagement_score || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Render Time Series Trends
    if (reportData.time_series_trends) {
      Object.entries(reportData.time_series_trends).forEach(([trendKey, trendData]: [string, any]) => {
        if (Array.isArray(trendData) && trendData.length > 0) {
          content.push(
            <div key={trendKey} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 capitalize">
                {trendKey.replace(/_/g, ' ')}
              </h2>
              <div className="space-y-2">
                {trendData.map((item: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <span className="text-sm font-medium">Period {index + 1}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-sm">Value: <strong>{item.value}</strong></span>
                      <span className="text-sm">Change: <strong>{item.change > 0 ? '+' : ''}{item.change}</strong></span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        item.trend === 'up' ? 'bg-green-100 text-green-800' :
                        item.trend === 'down' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.trend}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }
      });
    }

    // Render User Progress with special handling
    if (reportData.dimensional_breakdowns?.by_user && reportData.dimensional_breakdowns.by_user.length > 0) {
      content.push(
        <div key="users" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">User Progress</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">In Progress</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completion Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engagement</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Top Skills</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.dimensional_breakdowns.by_user.map((user: any, index: number) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.user_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{user.metrics?.courses_assigned || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{user.metrics?.courses_in_progress || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{user.metrics?.courses_completed || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{user.metrics?.completion_rate?.toFixed(2)}%</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{user.metrics?.total_time_hours?.toFixed(2)}h</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{user.metrics?.engagement_score || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={user.metrics?.skills_acquired?.join(', ')}>
                      {user.metrics?.skills_acquired && user.metrics.skills_acquired.length > 0
                        ? user.metrics.skills_acquired.slice(0, 3).join(', ')
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Render Role Performance with special handling
    if (reportData.dimensional_breakdowns?.by_role && reportData.dimensional_breakdowns.by_role.length > 0) {
      content.push(
        <div key="roles" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Role Performance</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Users</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completion Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Top Skills</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.dimensional_breakdowns.by_role.map((role: any, index: number) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{role.role_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{role.total_users}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{role.metrics?.avg_completion_rate?.toFixed(2)}%</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{role.metrics?.total_time_hours?.toFixed(2)}h</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {role.metrics?.top_skills && role.metrics.top_skills.length > 0
                        ? role.metrics.top_skills.slice(0, 3).join(', ')
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Render other dimensional breakdowns generically
    if (reportData.dimensional_breakdowns) {
      Object.entries(reportData.dimensional_breakdowns).forEach(([key, value]: [string, any]) => {
        if (key !== 'by_department' && key !== 'by_role' && key !== 'by_user' && Array.isArray(value) && value.length > 0) {
          content.push(
            <div key={key} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 capitalize">
                {key.replace(/_/g, ' ')}
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(value[0]).filter(k => k !== 'metrics').map((header) => (
                        <th
                          key={header}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {header.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {value.map((row: any, index: number) => (
                      <tr key={index} className="hover:bg-gray-50">
                        {Object.entries(row).filter(([k]) => k !== 'metrics').map(([cellKey, cell]: [string, any], cellIndex: number) => (
                          <td key={cellIndex} className="px-4 py-3 text-sm text-gray-900">
                            {typeof cell === 'number'
                              ? cell.toLocaleString(undefined, { maximumFractionDigits: 2 })
                              : typeof cell === 'boolean'
                              ? cell ? 'Yes' : 'No'
                              : Array.isArray(cell)
                              ? cell.join(', ')
                              : typeof cell === 'object' && cell !== null
                              ? Object.keys(cell).length + ' items'
                              : cell || 'N/A'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        } else if (!Array.isArray(value)) {
          content.push(
            <div key={key} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 capitalize">
                {key.replace(/_/g, ' ')}
              </h2>
              <div className="space-y-2">
                {Object.entries(value).map(([subKey, subValue]) => (
                  <div key={subKey} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                    <span className="text-sm text-gray-600 capitalize">
                      {subKey.replace(/_/g, ' ')}
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {typeof subValue === 'number'
                        ? subValue.toLocaleString()
                        : typeof subValue === 'boolean'
                        ? subValue
                          ? 'Yes'
                          : 'No'
                        : typeof subValue === 'object' && subValue !== null
                        ? JSON.stringify(subValue)
                        : subValue || 'N/A'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        }
      });
    }

    return content.length > 0 ? content : (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <p className="text-gray-500">Report data is available for download via PDF or Excel export.</p>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/reporting/reports/saved')}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{report.report_name}</h1>
            {report.description && (
              <p className="text-sm text-gray-500 mt-1">{report.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ExportButtons
            onExportPDF={handleExportPDF}
            onExportExcel={handleExportExcel}
            variant="dropdown"
          />
          <button
            onClick={handleDelete}
            disabled={deleteReport.isPending}
            className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 disabled:opacity-50 flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Metadata */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-500">Report Type</div>
            <div className="text-sm font-medium text-gray-900 mt-1">
              {formatReportType(report.report_type)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Period</div>
            <div className="text-sm font-medium text-gray-900 mt-1 capitalize">
              {(report as any).period?.period_type || report.period_type || 'N/A'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Generated</div>
            <div className="text-sm font-medium text-gray-900 mt-1">
              {formatDate((report as any).created_at || report.generated_at)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Report ID</div>
            <div className="text-xs font-mono text-gray-900 mt-1">{(report as any).report_id || report.snapshot_id}</div>
          </div>
        </div>
        {report.tags && report.tags.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-wrap gap-2">
              {report.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Report Content */}
      <div className="space-y-6">{renderReportContent()}</div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Report"
        message="Are you sure you want to delete this report?"
        isLoading={deleteReport.isPending}
      />
    </div>
  );
}
